import { debounce } from "lodash-es";
import * as React from "react";
import { NavLink } from "react-router-dom";
import styled, { ThemeProvider, css } from "styled-components";

import { SvgSprite } from "./SvgSprite";

const CarouselPagingControl = styled.button`
  position: absolute;
  top: 0;
  height: 3rem;
  background-color: transparent;
  border: 0;
  color: ${(p) => p.theme.arrowColor as string};
  padding: 0 1rem;
  display: flex;
  align-items: center;
  transition: all 0.15s ${(p): string => p.theme.timingFnEaseOut};
`;

interface NavBarState {
  clientWidth: number;
  scrollWidth: number;
  scrollLeft: number;
}

type NavBarActions =
  | {
      type: "UPDATE_SCROLL_LEFT";
      payload: {
        scrollLeft: number;
      };
    }
  | {
      type: "HANDLE_RESIZE";
      payload: {
        clientWidth: number;
        scrollWidth: number;
        scrollLeft: number;
      };
    };

const reducer = (state: NavBarState, action: NavBarActions): NavBarState => {
  switch (action.type) {
    case "UPDATE_SCROLL_LEFT":
      return {
        ...state,
        scrollLeft: action.payload.scrollLeft,
      };
    case "HANDLE_RESIZE":
      return {
        ...state,
        clientWidth: action.payload.clientWidth,
        scrollWidth: action.payload.scrollWidth,
        scrollLeft: action.payload.scrollLeft,
      };
    default:
      return state;
  }
};

export const NavBar = ({ children }): JSX.Element => {
  const [state, dispatch] = React.useReducer(reducer, {
    clientWidth: 0,
    scrollWidth: 0,
    scrollLeft: 0,
  });

  const scrollEl = React.useRef<HTMLUListElement>(null);

  const handleScroll = React.useMemo(() => {
    const handleScroll = (): void => {
      const scrollLeft = Math.floor(scrollEl.current.scrollLeft);

      dispatch({
        type: "UPDATE_SCROLL_LEFT",
        payload: {
          scrollLeft,
        },
      });
    };

    return debounce(handleScroll, 300);
  }, []);

  /* Handle carousel clicks */
  const handlePreviousClick = React.useCallback(
    (e) => {
      e.preventDefault();
      scrollEl.current.scrollBy({ left: -100, behavior: "smooth" });
    },
    [scrollEl]
  );

  const handleNextClick = React.useCallback(
    (e) => {
      e.preventDefault();
      scrollEl.current.scrollBy({ left: 100, behavior: "smooth" });
    },
    [scrollEl]
  );

  /* Handle resize events */
  React.useEffect(() => {
    const handleResize = (e?: UIEvent): void => {
      const { clientWidth, scrollLeft, scrollWidth } = scrollEl.current;

      dispatch({
        type: "HANDLE_RESIZE",
        payload: {
          clientWidth,
          scrollLeft,
          scrollWidth,
        },
      });
    };

    const debouncedHandleResize = debounce(handleResize, 300);

    // run function on component load
    handleResize();

    window.addEventListener("resize", debouncedHandleResize, false);

    return (): void => {
      window.removeEventListener("resize", debouncedHandleResize, false);
    };
  }, []);

  return (
    <ThemeProvider
      // we're adding new theme variables here so it's okay to ignore typescript
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      theme={({ primaryColor, secondaryColor, ...rest }) => ({
        ...rest,
        tabActiveColor: rest.tabActiveColor ?? "#fff",
        tabActiveBgColor: rest.tabActiveBgColor ?? primaryColor,
        tabFocusBgColor: rest.tabFocusBgColor ?? secondaryColor,
        tabColor: rest.tabColor ?? "#fff",
        tabBgColor: rest.tabBgColor ?? "transparent",
        tabBorderRadius: rest.tabBorderRadius ?? 0,
        barBgColor: rest.barBgColor ?? "#5B2084",
        arrowColor: rest.arrowColor ?? "#fff",
        arrowBgColor: rest.arrowBgColor ?? "#5B2084",
        borderBottomWidth: rest.borderBottomWidth ?? 6,
        borderBottomColor: rest.borderBottomColor ?? primaryColor,
        barBorderRadius: rest.barBorderRadius ?? 0,
        isFluid: rest.isFluid ?? true,
        paddingX: rest.paddingX ?? "1rem",
        marginTop: rest.marginTop ?? 0,
      })}
    >
      <div
        className="navbar-wrapper navbar-primary"
        css={`
          position: relative;
          margin-top: ${(p): number => p.theme.marginTop / 16}rem;
          overflow: hidden;
          background-color: ${(p): string => p.theme.barBgColor};
          border-bottom: ${(p): number => p.theme.borderBottomWidth}px solid
            ${(p): string => p.theme.borderBottomColor};
          border-radius: ${(p) => p.theme.barBorderRadius};

          // Ensure the updated value is used see /developer page.
          &.navbar-wrapper::before {
            content: ${(p): string =>
              p.theme.enableBackgroundPatterns === "true" ? "" : "none"};
          }
        `}
      >
        <div
          css={`
            margin: 0 auto;

            padding-left: ${(p): string => `${p.theme.paddingX}`};
            padding-right: ${(p): string => `${p.theme.paddingX}`};

            @media (min-width: ${(p): string => p.theme.mdBreakpoint}) {
              max-width: ${(p): string => p.theme.mdMaxWidth};
            }

            @media (min-width: ${(p): string => p.theme.lgBreakpoint}) {
              max-width: ${(p): string => p.theme.lgMaxWidth};
            }

            @media (min-width: ${(p): string => p.theme.xlBreakpoint}) {
              max-width: ${(p): string => p.theme.xlMaxWidth};
            }
          `}
        >
          <div
            css={`
              position: relative;
              height: 100%;

              ${(p) =>
                p.theme.isFluid &&
                css`
                  margin-left: -${(p): string => `${p.theme.paddingX}`};
                  margin-right: -${(p): string => `${p.theme.paddingX}`};

                  @media (min-width: 768px) {
                    margin: 0 0;
                  }
                `}
            `}
          >
            <ul
              onScroll={handleScroll}
              ref={scrollEl}
              css={`
                display: flex;
                margin: 0;
                padding-inline-start: 0;
                overflow-x: auto;
                overflow-y: hidden;
                white-space: nowrap;
                list-style: none;
                -webkit-overflow-scrolling: touch;
                overflow-scrolling: touch;
                scroll-snap-type: x mandatory;
                scroll-snap-destination: 50% 50%;

                /* Hide scrollbar for Firefox */
                scrollbar-width: none;

                /* Hide scrollbar for IE 10+ */
                -ms-overflow-style: none;

                /* Hide scrollbar for WebKit (Chrome, Safari, Edge) */
                &::-webkit-scrollbar {
                  display: none;
                }
              `}
            >
              {children}
            </ul>
          </div>
        </div>

        {/* Directional Arrows */}
        <CarouselPagingControl
          tabIndex={-1}
          aria-hidden="true"
          type="button"
          onClick={handlePreviousClick}
          style={
            state.scrollLeft === 0
              ? {
                  opacity: 0,
                  visibility: "hidden",
                }
              : {}
          }
          css={`
            background-image: linear-gradient(
              270deg,
              transparent 0%,
              ${(p): string => p.theme.arrowBgColor} 35%
            );
            left: 0;
          `}
        >
          <SvgSprite
            id="icons:chev-left"
            width={16}
            height={16}
            aria-label="Previous"
          />
        </CarouselPagingControl>
        <CarouselPagingControl
          tabIndex={-1}
          aria-hidden="true"
          type="button"
          onClick={handleNextClick}
          style={
            state.scrollLeft + state.clientWidth === state.scrollWidth
              ? {
                  opacity: 0,
                  visibility: "hidden",
                }
              : {}
          }
          css={`
            background-image: linear-gradient(
              90deg,
              transparent 0%,
              ${(p): string => p.theme.arrowBgColor} 35%
            );
            right: 0;
          `}
        >
          <SvgSprite
            id="icons:chev-right"
            width={16}
            height={16}
            aria-label="Next"
          />
        </CarouselPagingControl>
      </div>
    </ThemeProvider>
  );
};

NavBar.ListItemLink = styled(NavLink)`
  position: relative;
  padding: 0 3rem;
  height: 3rem;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${(p): string => `${p.theme.tabColor}`};
  background-color: ${(p): string => `${p.theme.tabBgColor}`};
  border-top-left-radius: ${(p): number =>
    (p.theme.tabBorderRadius as number) / 16}rem;
  border-top-right-radius: ${(p): number =>
    (p.theme.tabBorderRadius as number) / 16}rem;
  white-space: nowrap;

  &:after {
    content: attr(data-text);
    height: 0;
    visibility: hidden;
    overflow: hidden;
    user-select: none;
    pointer-events: none;
    font-weight: 700;
    @media speech {
      display: none;
    }
  }

  &:hover {
    color: ${(p): string => `${p.theme.tabColor}`};
    text-decoration: none;
    font-weight: 700;
  }

  &.active {
    font-weight: 700;
    color: ${(p): string => `${p.theme.tabActiveColor}`};
    background-color: ${(p): string => `${p.theme.tabActiveBgColor}`};
  }

  &:focus {    
    &:not(.active) {
      background-color: transparent;
    }
  }
`;

NavBar.ListItem = styled.li`
  display: block;
  scroll-snap-coordinate: 50% 50%;
  scroll-snap-align: center;
`;
